import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { trackingService } from '../../services/tracking.service';
import { LEAD_FIELDS, fmtMoney, fmtNum } from './tracking-page.format';

interface Props {
  state: {
    label: string;
    loading: boolean;
    data: Awaited<ReturnType<typeof trackingService.getLeadBreakdown>> | null;
  } | null;
  dateFrom: string;
  dateTo: string;
  onClose: () => void;
}

const isUnknown = (v: string) => v === '(unknown)' || v === 'unknown' || v === '';

export default function LeadBreakdownDialog({ state, dateFrom, dateTo, onClose }: Props) {
  return (
    <Dialog open={!!state} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Lead breakdown
        <Typography variant="body2" color="text.secondary">
          {state?.label} · {dateFrom} → {dateTo}
          {state?.data ? ` · ${fmtNum(state.data.total_conversions)} conversions` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {state?.loading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
        {state?.data && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            {LEAD_FIELDS.map((f) => {
              const rows = state.data!.fields[f.key] || [];
              const total = rows.reduce((a, r) => a + r.conversions, 0) || 1;
              // A field the source never sends collapses to a single unknown bucket. Say
              // that plainly instead of drawing a 100% bar, which would read as data
              // rather than as an absence.
              const notProvided = rows.length > 0 && rows.every((r) => isUnknown(r.value));
              return (
                <Box key={f.key}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{f.label}</Typography>
                  {rows.length === 0 && <Typography variant="caption" color="text.secondary">No data.</Typography>}
                  {notProvided && (
                    <Typography variant="caption" color="text.secondary">
                      Not sent by this source's postbacks.
                    </Typography>
                  )}
                  {!notProvided && rows.slice(0, 8).map((r) => {
                    const pctOf = (r.conversions / total) * 100;
                    // A literal {macro} means the network sent the placeholder unexpanded
                    // — a setup bug, not a value. Flag it as such.
                    const unexpanded = r.value.includes('{') || r.value.includes('%7B');
                    return (
                      <Box key={r.value} sx={{ mb: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ maxWidth: '55%', color: unexpanded ? 'warning.dark' : 'inherit', fontWeight: unexpanded ? 600 : 400 }}
                            title={unexpanded ? 'Unexpanded macro — the network sent the placeholder literally' : r.value}
                          >
                            {unexpanded ? `⚠ ${r.value}` : r.value}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtNum(r.conversions)} · {pctOf.toFixed(0)}% · {fmtMoney(r.revenue)}
                          </Typography>
                        </Box>
                        {/* share bar — magnitude at a glance without a chart library */}
                        <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover' }}>
                          <Box sx={{ height: 4, borderRadius: 2, width: `${pctOf}%`, bgcolor: 'primary.main' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
